import { useNavigate } from "react-router-dom";
import { auth, db } from "../config/firebase";
import { signOut } from "firebase/auth";
import { doc } from "firebase/firestore";
import { useAuth } from "../contexts/AuthContext";
import { saveUserConditions, uploadUserPhotos } from "../services/userService";
import { createChatRoom, switchToStaffChat, subscribeMessages, sendMessage } from "../services/chatService";
import { createTaikenApplication } from "../services/taikenService";
import { useState, useEffect, useRef } from "react";
import { ICON_SM, ICON_MD } from "../constants/icons";




const QUESTIONS = [
  { id: "greeting", text: "はじめまして！ドレスワークへようこそ。\nあなたにぴったりのお店を見つけるために、いくつか質問させてくださいね。\n回答後は専任スタッフがあなた専属でサポートしますので、安心してお答えください。", type: "info" },
  { id: "experience", text: "まず、ナイトワークのご経験はありますか？", type: "select", options: ["未経験です", "半年未満", "半年〜1年", "1年以上"] },
  { id: "area", text: "ありがとうございます。\n希望の勤務エリアを教えてください。", type: "select", options: ["新宿・歌舞伎町", "六本木・西麻布", "銀座", "渋谷・恵比寿", "池袋", "大阪", "京都", "その他"] },
  { id: "area_other", text: "希望のエリアを入力してください。", type: "text", condition: (a) => a.area === "その他" },
  { id: "salary_beginner", text: "希望の時給はどのくらいですか？", type: "select", options: ["3,000円〜", "4,000円〜", "5,000円〜", "6,000円以上", "特にこだわらない"], condition: (a) => a.experience === "未経験です" },
  { id: "salary_exp", text: "希望の時給を入力してください。\n（例：7,000円〜）", type: "text", condition: (a) => a.experience !== "未経験です" },
  { id: "schedule", text: "出勤できる曜日・時間帯を教えてください。", type: "multi", options: ["平日メイン", "週末メイン", "週1〜2日", "週3〜4日", "フルタイム"] },
  { id: "priority", text: "お店選びで一番大事にしたいことは？", type: "select", options: ["お給料の高さ", "雰囲気の良さ・働きやすさ", "未経験へのサポート", "自由なシフト", "客層の良さ"] },
  { id: "body_intro", text: "ありがとうございます。\nお店へのご紹介のために、年齢・身長・体重を教えていただけますか？", type: "info" },
  { id: "bodyinfo", text: "年齢・身長・体重を入力してください。", type: "bodyinfo" },
  { id: "photo_request", text: "お顔がわかる写真を2枚お送りいただけますか？\nお店への紹介時に使用します。加工アプリの写真でもOKです。", type: "photo" },
  { id: "user_name", text: "最後に、お名前を教えてください。", type: "text" },
  { id: "complete", text: "ありがとうございます。\nいただいた条件をもとに、担当スタッフが店舗への確認を行いますので、少しだけお時間をいただきます。\n確認でき次第、ぴったりのお店をご紹介しますので、もうしばらくお待ちください。\n\nその他にご希望やご質問があれば、お気軽にこちらのチャットでお伝えくださいね。", type: "complete" },
];

function Bubble({ from, children, animate }) {
  const isBot = from === "bot";
  return (
    <div className={`dw-bubble ${isBot ? "dw-bubble--bot" : "dw-bubble--user"}`} style={{ animation: animate ? "dwFadeUp 0.4s ease both" : "none" }}>
      {isBot && <div className="dw-bubble__avatar"><img src={ICON_SM} alt="" /></div>}
      <div className={`dw-bubble__body ${isBot ? "dw-bubble__body--bot" : "dw-bubble__body--user"}`}>{children}</div>
    </div>
  );
}

function OptionChips({ options, onSelect, multi }) {
  const [selected, setSelected] = useState([]);
  const toggle = (opt) => { if (multi) { setSelected(p => p.includes(opt) ? p.filter(o => o !== opt) : [...p, opt]); } else { onSelect(opt); } };
  return (
    <div className="dw-chips" style={{ animation: "dwFadeUp 0.4s ease 0.15s both" }}>
      {options.map(opt => (
        <button key={opt} onClick={() => toggle(opt)} className={`dw-chip ${selected.includes(opt) ? "dw-chip--active" : ""}`}>{opt}</button>
      ))}
      {multi && selected.length > 0 && <button onClick={() => onSelect(selected.join("、"))} className="dw-chip dw-chip--confirm">決定 ✓</button>}
    </div>
  );
}

function TextInput({ onSubmit }) {
  const [val, setVal] = useState("");
  const valid = val.trim();
  return (
    <div className="dw-text-input" style={{ animation: "dwFadeUp 0.4s ease 0.15s both" }}>
      <input value={val} onChange={e => setVal(e.target.value)} onKeyDown={e => e.key === "Enter" && valid && onSubmit(val.trim())} placeholder="入力してください..." className="dw-text-input__field" />
      <button onClick={() => valid && onSubmit(val.trim())} className={`dw-text-input__btn ${!valid ? "dw-text-input__btn--disabled" : ""}`} disabled={!valid}>送信</button>
    </div>
  );
}

function BodyInfoInput({ onSubmit }) {
  const [age, setAge] = useState("");
  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");
  const valid = age.trim() && height.trim() && weight.trim();
  return (
    <div className="dw-body-input" style={{ animation: "dwFadeUp 0.4s ease 0.15s both" }}>
      <div className="dw-body-input__row">
        <div className="dw-body-input__field">
          <label className="dw-body-input__label">年齢</label>
          <div className="dw-body-input__wrap">
            <input type="number" value={age} onChange={e => setAge(e.target.value)} placeholder="22" className="dw-body-input__input" />
            <span className="dw-body-input__unit">歳</span>
          </div>
        </div>
        <div className="dw-body-input__field">
          <label className="dw-body-input__label">身長</label>
          <div className="dw-body-input__wrap">
            <input type="number" value={height} onChange={e => setHeight(e.target.value)} placeholder="160" className="dw-body-input__input" />
            <span className="dw-body-input__unit">cm</span>
          </div>
        </div>
        <div className="dw-body-input__field">
          <label className="dw-body-input__label">体重</label>
          <div className="dw-body-input__wrap">
            <input type="number" value={weight} onChange={e => setWeight(e.target.value)} placeholder="50" className="dw-body-input__input" />
            <span className="dw-body-input__unit">kg</span>
          </div>
        </div>
      </div>
      <button onClick={() => valid && onSubmit(`${age}歳 / ${height}cm / ${weight}kg`)} className={`dw-body-input__btn ${!valid ? "dw-body-input__btn--disabled" : ""}`} disabled={!valid}>送信</button>
    </div>
  );
}

function PhotoInput({ onSubmit }) {
  const [photos, setPhotos] = useState([null, null]);
  const [names, setNames] = useState(["", ""]);
  const handleFile = (idx) => (e) => {
    try {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!file.type || !file.type.startsWith("image/")) {
        console.warn("Not an image file:", file.type);
        return;
      }
      const newNames = [...names]; newNames[idx] = file.name; setNames(newNames);
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const newPhotos = [...photos]; newPhotos[idx] = ev.target.result; setPhotos(newPhotos);
        } catch (err) { console.error("Photo set error:", err); }
      };
      reader.onerror = () => { console.error("File read error"); };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error("Photo select error:", err);
    }
    if (e.target) e.target.value = "";
  };
  const bothDone = photos[0] && photos[1];
  return (
    <div className="dw-photo-input" style={{ animation: "dwFadeUp 0.4s ease 0.15s both" }}>
      <div className="dw-photo-input__grid">
        {[0, 1].map(idx => (
          <div key={idx} className="dw-photo-input__slot">
            <div className="dw-photo-input__slot-label">{idx === 0 ? "1枚目" : "2枚目"}</div>
            {!photos[idx] ? (
              <label className="dw-photo-input__upload">
                <span className="dw-photo-input__icon">📷</span>
                <span className="dw-photo-input__text">タップして選択</span>
                <input type="file" accept="image/*" onChange={handleFile(idx)} style={{ display: "none" }} />
              </label>
            ) : (
              <div className="dw-photo-input__done">
                <img src={photos[idx]} alt="preview" className="dw-photo-input__preview" />
                <label className="dw-photo-input__change">
                  変更
                  <input type="file" accept="image/*" onChange={handleFile(idx)} style={{ display: "none" }} />
                </label>
              </div>
            )}
          </div>
        ))}
      </div>
      {bothDone && <button onClick={() => onSubmit(`写真2枚を送信`, photos)} className="dw-photo-input__send">2枚の写真を送信</button>}

    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="dw-bubble dw-bubble--bot" style={{ animation: "dwFadeUp 0.3s ease both" }}>
      <div className="dw-bubble__avatar"><img src={ICON_SM} alt="" /></div>
      <div className="dw-bubble__body dw-bubble__body--bot dw-typing"><div className="dw-typing__dot" /><div className="dw-typing__dot" /><div className="dw-typing__dot" /></div>
    </div>
  );
}

function SummaryCard({ answers }) {
  const labels = { experience: "経験", area: "エリア", area_other: "希望エリア", salary_beginner: "希望時給", salary_exp: "希望時給", schedule: "出勤", priority: "重視ポイント", bodyinfo: "年齢・身長・体重", photo_request: "写真", user_name: "お名前" };
  return (
    <div className="dw-summary" style={{ animation: "dwFadeUp 0.5s ease 0.3s both" }}>
      <div className="dw-summary__tag">あなたの希望条件</div>
      {Object.entries(answers).filter(([k]) => labels[k]).map(([k, v]) => (
        <div key={k} className="dw-summary__row"><span className="dw-summary__label">{labels[k]}</span><span className="dw-summary__value">{v}</span></div>
      ))}
    </div>
  );
}


function TaikenApplyModal({ job, userId, onClose }) {
  const [phone, setPhone] = useState("");
  const [lineId, setLineId] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const canSubmit = phone.trim() || lineId.trim();

useEffect(() => {
    if (!userId || loaded) return;
    (async () => {
      try {
        const { getDoc } = await import("firebase/firestore");
        const snap = await getDoc(doc(db, "users", userId));
        if (snap.exists()) {
          const data = snap.data();
          if (data.phone) setPhone(data.phone);
          if (data.lineId) setLineId(data.lineId);
        }
      } catch (e) { console.error("Load contact error:", e); }
      setLoaded(true);
    })();
  }, [userId, loaded]);

  const handleSubmit = async () => {
    if (!canSubmit || sending) return;
    setSending(true);
    try {
      await createTaikenApplication({
        userId,
        jobId: job.jobId || "",
        shopName: job.shopName || "",
        area: job.area || "",
        preferredDate: "" });
      // Save contact info to user doc
      const { setDoc, serverTimestamp } = await import("firebase/firestore");
      const contactUpdate = { updatedAt: serverTimestamp() };
      if (phone.trim()) contactUpdate.phone = phone.trim();
      if (lineId.trim()) contactUpdate.lineId = lineId.trim();
      await setDoc(doc(db, "users", userId), contactUpdate, { merge: true });
      // Send notification message in chat
      await sendMessage(userId, {
        senderId: userId,
        senderType: "user",
        text: `【体験入店申込】${job.shopName}（${job.area}）に体験入店を希望します。\n電話番号: ${phone.trim() || "未入力"}\nLINE: ${lineId.trim() || "未入力"}`,
        type: "text" });
      setDone(true);
    } catch (e) {
      console.error("Taiken apply error:", e);
      alert("申し込みに失敗しました。");
    }
    setSending(false);
  };

  return (
    <div className="dw-taiken-overlay" onClick={onClose}>
      <div className="dw-taiken-modal" onClick={e => e.stopPropagation()}>
        {!done ? (<>
          <div className="dw-taiken-modal__header">
            <h3>体験入店の申し込み</h3>
            <button className="dw-taiken-modal__close" onClick={onClose}>×</button>
          </div>
          <div className="dw-taiken-modal__body">
            <div className="dw-taiken-modal__shop">{job.shopName}（{job.area}）</div>
            <p className="dw-taiken-modal__desc">日程調整のためご連絡先を教えてください。</p>
            <div className="dw-taiken-modal__field">
              <label>電話番号</label>
              <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="090-1234-5678" />
            </div>
            <div className="dw-taiken-modal__field">
              <label>LINE ID（任意）</label>
              <input value={lineId} onChange={e => setLineId(e.target.value)} placeholder="your_line_id" />
            </div>
            <button className="dw-taiken-modal__btn" disabled={!canSubmit || sending} style={{opacity: canSubmit && !sending ? 1 : 0.3}} onClick={handleSubmit}>
              {sending ? "送信中..." : "申し込む"}
            </button>
          </div>
        </>) : (
          <div className="dw-taiken-modal__body dw-taiken-modal__done">
            <div className="dw-taiken-modal__check">✓</div>
            <h3>申し込み完了</h3>
            <p>担当スタッフから日程のご連絡をお送りします。</p>
            <button className="dw-taiken-modal__btn" onClick={onClose}>閉じる</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function DressWorkChat() {
  const [messages, setMessages] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [typing, setTyping] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [freeMessages, setFreeMessages] = useState([]);
  const [freeMsg, setFreeMsg] = useState("");
  const [checkingStatus, setCheckingStatus] = useState(true);
  const scrollRef = useRef(null);
  const { currentUser } = useAuth();
  const [notification, setNotification] = useState(null);

  // Request push notification permission after login
  useEffect(() => {
    if (!currentUser) return;
    const setupPush = async () => {
      try {
        const { requestNotificationPermission, onForegroundMessage } = await import("../services/pushService");
        await requestNotificationPermission(currentUser.uid);
        onForegroundMessage((payload) => {
          setNotification({
            title: payload.notification?.title || "DressWork",
            body: payload.notification?.body || "新しいメッセージがあります" });
          setTimeout(() => setNotification(null), 5000);
        });
      } catch (e) {
        console.log("Push setup skipped:", e);
      }
    };
    setupPush();
  }, [currentUser]);
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [taikenModal, setTaikenModal] = useState(null);
  const [viewPhoto, setViewPhoto] = useState(null);

  const handleLogout = async () => {
    if (window.confirm("ログアウトしますか？")) {
      await signOut(auth);
      navigate("/login");
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm("本当に退会しますか？\nアカウントと全てのデータが削除されます。この操作は取り消せません。")) return;
    if (!window.confirm("最終確認：退会してよろしいですか？")) return;
    try {
      const { getFunctions, httpsCallable } = await import("firebase/functions");
      const { default: app } = await import("../config/firebase");
      const functions = getFunctions(app, "us-central1");
      const deleteAccount = httpsCallable(functions, "deleteUserAccount");
      await deleteAccount();
      await signOut(auth);
      navigate("/login");
    } catch (e) {
      console.error("Delete account error:", e);
      alert("退会処理に失敗しました。再度ログインしてからお試しください。");
    }
  };
  const hasSaved = useRef(false);

  // Check if user already completed questionnaire
  useEffect(() => {
    if (!currentUser) { setCheckingStatus(false); return; }
    const checkUser = async () => {
      try {
        const { getDoc } = await import("firebase/firestore");
        const userSnap = await getDoc(doc(db, "users", currentUser.uid));
        if (userSnap.exists()) {
          const data = userSnap.data();
          if (data.status && data.status !== "新規登録") {
            // Rebuild past Q&A from saved conditions
            const pastMessages = [];
            pastMessages.push({ from: "bot", text: "はじめまして！ドレスワークへようこそ。\nあなたにぴったりのお店を見つけるために、いくつか質問させてくださいね。" });
            if (data.experience) { pastMessages.push({ from: "bot", text: "まず、ナイトワークのご経験はありますか？" }); pastMessages.push({ from: "user", text: data.experience }); }
            if (data.area) { pastMessages.push({ from: "bot", text: "ありがとうございます。\n希望の勤務エリアを教えてください。" }); pastMessages.push({ from: "user", text: data.area }); }
            if (data.salary) { pastMessages.push({ from: "bot", text: data.experience === "未経験です" ? "希望の時給はどのくらいですか？" : "希望の時給を入力してください。" }); pastMessages.push({ from: "user", text: data.salary }); }
            if (data.schedule) { pastMessages.push({ from: "bot", text: "出勤できる曜日・時間帯を教えてください。" }); pastMessages.push({ from: "user", text: data.schedule }); }
            if (data.priority) { pastMessages.push({ from: "bot", text: "お店選びで一番大事にしたいことは？" }); pastMessages.push({ from: "user", text: data.priority }); }
            if (data.height || data.weight) { pastMessages.push({ from: "bot", text: "お店へのご紹介のために、年齢・身長・体重を教えていただけますか？" }); pastMessages.push({ from: "user", text: `${data.ageRange || ""}${data.ageRange ? " / " : ""}${data.height || ""} / ${data.weight || ""}` }); }
            if (data.name) { pastMessages.push({ from: "bot", text: "最後に、お名前を教えてください。" }); pastMessages.push({ from: "user", text: data.name }); }
            pastMessages.push({ from: "bot", text: "ありがとうございます。\nいただいた条件をもとに、担当スタッフが店舗への確認を行いますので、少しだけお時間をいただきます。\n確認でき次第、ぴったりのお店をご紹介しますので、もうしばらくお待ちくださいね。" });

            // Build answers for summary card
            const savedAnswers = {};
            if (data.experience) savedAnswers.experience = data.experience;
            if (data.area) savedAnswers.area = data.area;
            if (data.salary) savedAnswers[data.experience === "未経験です" ? "salary_beginner" : "salary_exp"] = data.salary;
            if (data.schedule) savedAnswers.schedule = data.schedule;
            if (data.priority) savedAnswers.priority = data.priority;
            if (data.height || data.weight) savedAnswers.bodyinfo = `${data.ageRange || ""}${data.ageRange ? " / " : ""}${data.height || ""} / ${data.weight || ""}`;
            if (data.name) savedAnswers.user_name = data.name;

            setMessages(pastMessages);
            setAnswers(savedAnswers);
            setIsComplete(true);
            setCheckingStatus(false);
            return;
          }
        }
      } catch (e) {
        console.error("Status check error:", e);
      }
      setCheckingStatus(false);
    };
    checkUser();
  }, [currentUser]);

  // Save conditions to Firestore when chat completes
useEffect(() => {
    if (isComplete && currentUser && !hasSaved.current) {
      hasSaved.current = true;
      const save = async () => {
        try {
          // Ensure chatRoom exists with ai_chat phase before switching
          await createChatRoom(currentUser.uid);
          await saveUserConditions(currentUser.uid, answers);
          // Upload photos to Firebase Storage
          if (answers._photoFiles && answers._photoFiles.length > 0) {
            const files = [];
            for (const dataUrl of answers._photoFiles) {
              const res = await fetch(dataUrl);
              const blob = await res.blob();
              files.push(new File([blob], "photo_" + Date.now() + ".jpg", { type: "image/jpeg" }));
            }
            await uploadUserPhotos(currentUser.uid, files);
          }
          await switchToStaffChat(currentUser.uid);
        } catch (e) {
          console.error("Error saving conditions:", e);
        }
      };
      save();
    }
  }, [isComplete, currentUser, answers]);

  // Subscribe to real-time messages after questionnaire completes
  useEffect(() => {
    if (!isComplete || !currentUser) return;
    const unsubscribe = subscribeMessages(currentUser.uid, (msgs) => {
      setFreeMessages(msgs);
      scrollBottom();
    });
    return () => unsubscribe();
  }, [isComplete, currentUser]);

  const sendFreeMessage = async () => {
    if (!freeMsg.trim() || !currentUser) return;
    const text = freeMsg.trim();
    setFreeMsg("");
    try {
      await sendMessage(currentUser.uid, {
        senderId: currentUser.uid,
        senderType: "user",
        text,
        type: "text" });
    } catch (e) {
      console.error("Send message error:", e);
    }
  };

  const sendImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !currentUser) return;
    try {
      const { compressImage } = await import("../services/imageUtil");
      const compressed = await compressImage(file).catch(() => file);
      const { ref, uploadBytes, getDownloadURL } = await import("firebase/storage");
      const { storage } = await import("../config/firebase");
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const storageRef = ref(storage, "chatImages/" + currentUser.uid + "/" + Date.now() + "_" + safeName);
      const snap = await uploadBytes(storageRef, compressed);
      const url = await getDownloadURL(snap.ref);
      await sendMessage(currentUser.uid, {
        senderId: currentUser.uid,
        senderType: "user",
        text: "",
        type: "image",
        metadata: { imageUrl: url },
      });
    } catch (err) {
      console.error("Image send error:", err);
    }
    if (e.target) e.target.value = "";
  };

  const scrollBottom = () => { setTimeout(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, 50); };

  const advanceQuestion = (nextIdx, updatedAnswers) => {
    let idx = nextIdx;
    while (idx < QUESTIONS.length) { if (QUESTIONS[idx].condition && !QUESTIONS[idx].condition(updatedAnswers)) { idx++; continue; } break; }
    if (idx >= QUESTIONS.length) { setIsComplete(true); return; }
    setCurrentQ(idx); setTyping(true); scrollBottom();
    setTimeout(() => {
      setTyping(false);
      const q = QUESTIONS[idx];
      setMessages(p => [...p, { from: "bot", text: q.text, id: q.id }]);
      if (q.type !== "info" && q.type !== "complete") setShowInput(true);
      if (q.type === "complete") setIsComplete(true);
      if (q.type === "info") setTimeout(() => advanceQuestion(idx + 1, updatedAnswers), 1200);
      scrollBottom();
    }, 900 + Math.random() * 400);
  };

  const handleAnswer = (answer, extraData) => {
    const q = QUESTIONS[currentQ];
    setShowInput(false);
    setMessages(p => [...p, { from: "user", text: answer }]);
    const newAnswers = { ...answers, [q.id]: answer };
    if (q.id === "photo_request" && extraData) {
      newAnswers._photoFiles = extraData;
    }
    setAnswers(newAnswers); scrollBottom();
    advanceQuestion(currentQ + 1, newAnswers);
  };

// eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!checkingStatus && !isComplete) { advanceQuestion(0, {}); } }, [checkingStatus, isComplete]);
  const currentQuestion = QUESTIONS[currentQ];

  if (checkingStatus) {
    return (
      <div className="dw-chat" style={{ alignItems: "center", justifyContent: "center" }}>
        <div className="dw-chat__header">
          <div className="dw-chat__header-icon"><img src={ICON_MD} alt="DressWork" /></div>
          <div><div className="dw-chat__header-name">DressWork</div><div className="dw-chat__header-sub">あなたにぴったりのお店を見つけます</div></div>
          <div className="dw-chat__header-status"><div className="dw-chat__header-dot" /><span>オンライン</span></div>
        </div>
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ color: "rgba(212,175,110,.4)", fontSize: "13px" }}>読み込み中...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="dw-chat">
      {notification && (
        <div className="dw-notif-toast" onClick={() => setNotification(null)}>
          <div className="dw-notif-toast__title">{notification.title}</div>
          <div className="dw-notif-toast__body">{notification.body}</div>
        </div>
      )}
      <div className="dw-chat__header">
        <div className="dw-chat__header-icon"><img src={ICON_MD} alt="DressWork" /></div>
        <div><div className="dw-chat__header-name">DressWork</div><div className="dw-chat__header-sub">あなたにぴったりのお店を見つけます</div></div>
        <div className="dw-chat__header-status"><div className="dw-chat__header-dot" /><span>オンライン</span></div>
        <button className="dw-chat__menu-btn" onClick={() => setShowMenu(!showMenu)}>⋮</button>
        {showMenu && (
          <div className="dw-chat__menu" onClick={() => setShowMenu(false)}>
            <button className="dw-chat__menu-item" onClick={() => navigate("/terms")}>利用規約</button>
            <button className="dw-chat__menu-item" onClick={() => navigate("/privacy")}>プライバシーポリシー</button>
            <div className="dw-chat__menu-divider" />
            <button className="dw-chat__menu-item" onClick={handleLogout}>ログアウト</button>
            <button className="dw-chat__menu-item dw-chat__menu-item--danger" onClick={handleDeleteAccount}>退会する</button>
          </div>
        )}
      </div>
      {!isComplete && (
        <div className="dw-chat__progress"><div className="dw-chat__progress-bar" style={{ width: `${(Object.keys(answers).length / QUESTIONS.filter(q => q.type !== "info" && q.type !== "complete").length) * 100}%` }} /></div>
      )}
      <div ref={scrollRef} className="dw-chat__body">
        {messages.map((msg, i) => {
          // Show SummaryCard before the complete message
          if (msg.id === "complete" && Object.keys(answers).length > 0) {
            return (
              <div key={i}>
                <SummaryCard answers={answers} />
                <Bubble from={msg.from} animate={i >= messages.length - 2}>{msg.text}</Bubble>
              </div>
            );
          }
          return <Bubble key={i} from={msg.from} animate={i >= messages.length - 2}>{msg.text}</Bubble>;
        })}
        {typing && <TypingIndicator />}
        {showInput && !typing && currentQuestion?.type === "select" && <OptionChips options={currentQuestion.options} onSelect={handleAnswer} />}
        {showInput && !typing && currentQuestion?.type === "multi" && <OptionChips options={currentQuestion.options} onSelect={handleAnswer} multi />}
        {showInput && !typing && currentQuestion?.type === "text" && <TextInput onSubmit={handleAnswer} />}
        {showInput && !typing && currentQuestion?.type === "bodyinfo" && <BodyInfoInput onSubmit={handleAnswer} />}
        {showInput && !typing && currentQuestion?.type === "photo" && <PhotoInput onSubmit={handleAnswer} />}

        {/* Free chat messages from Firestore */}
        {isComplete && freeMessages.map((msg, i) => (
          <Bubble key={"free-" + i} from={msg.senderType === "user" ? "user" : "bot"} animate={false}>
            {msg.type === "job_card" && msg.metadata ? (
              <div className="dw-job-card">
                {msg.metadata.photoUrls && msg.metadata.photoUrls.length > 0 && (
                  <div className={"dw-job-card__photos dw-job-card__photos--" + Math.min(msg.metadata.photoUrls.length, 3)}>
                    {msg.metadata.photoUrls.slice(0, 3).map((url, pi) => (
                      <img key={pi} src={url} alt="" className="dw-job-card__photo" onClick={(e) => { e.stopPropagation(); setViewPhoto(url); }} />
                    ))}
                  </div>
                )}
                <div className="dw-job-card__body">
                  <div className="dw-job-card__header">
                    <span className="dw-job-card__badge">求人紹介</span>
                    {msg.metadata.genre && <span className="dw-job-card__genre">{msg.metadata.genre}</span>}
                  </div>
                  <div className="dw-job-card__name">{msg.metadata.shopName}</div>
                  <div className="dw-job-card__details">
                    <div className="dw-job-card__detail"><span className="dw-job-card__icon">📍</span>{msg.metadata.area}</div>
                    <div className="dw-job-card__detail"><span className="dw-job-card__icon">💰</span>時給 {msg.metadata.salary}</div>
                  </div>
                  {msg.metadata.tags && msg.metadata.tags.length > 0 && (
                    <div className="dw-job-card__tags">
                      {msg.metadata.tags.map((tag, ti) => (
                        <span key={ti} className="dw-job-card__tag">{tag}</span>
                      ))}
                    </div>
                  )}
                  {msg.text && <div className="dw-job-card__comment">{msg.text}</div>}
                  <button className="dw-job-card__apply" onClick={(e) => { e.stopPropagation(); setTaikenModal(msg.metadata); }}>体験入店に申し込む</button>
                </div>
              </div>
            ) : msg.type === "image" && msg.metadata?.imageUrl ? (
              <img src={msg.metadata.imageUrl} alt="" className="dw-chat-img" onClick={() => setViewPhoto(msg.metadata.imageUrl)} />
            ) : msg.text}
          </Bubble>
        ))}
      </div>

      {/* Free chat input after questionnaire */}
      {isComplete ? (
        <div className="dw-free-chat">
          <label className="dw-free-chat__img-btn">
            📷
            <input type="file" accept="image/*" onChange={sendImage} style={{ display: "none" }} />
          </label>
          <textarea
            value={freeMsg}
            onChange={e => { setFreeMsg(e.target.value); e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px"; }}
            placeholder="メッセージを入力..."
            className="dw-free-chat__input"
            rows={1}
          />
          <button onClick={sendFreeMessage} className="dw-free-chat__btn">送信</button>
        </div>
      ) : (
        <div className="dw-chat__footer">© DressWork — あなたの新しいスタートを応援します</div>
      )}

      {viewPhoto && (
        <div className="dw-photo-viewer" onClick={() => setViewPhoto(null)}>
          <img src={viewPhoto} alt="" className="dw-photo-viewer__img" />
          <button className="dw-photo-viewer__close" onClick={() => setViewPhoto(null)}>×</button>
        </div>
      )}

      {taikenModal && <TaikenApplyModal job={taikenModal} userId={currentUser?.uid} onClose={() => setTaikenModal(null)} />}
      <style>{`
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;600&family=Zen+Kaku+Gothic+New:wght@400;500;700&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
:root{--gold:#d4af6e;--gold-dk:#c49a50;--gold-lt:#f0d48a;--dark:#1a1416;--dark-m:#2a2024;--ivory:#f5efe6;--fb:'Zen Kaku Gothic New','Noto Sans JP',-apple-system,sans-serif}

.dw-chat{max-width:430px;margin:0 auto;height:100vh;height:100dvh;display:flex;flex-direction:column;background:linear-gradient(170deg,var(--dark) 0%,var(--dark-m) 50%,#1e181c 100%);font-family:var(--fb);position:relative;overflow:hidden}

.dw-chat__header{padding:16px 18px 12px;display:flex;align-items:center;gap:10px;border-bottom:1px solid rgba(212,175,110,.08);background:rgba(26,20,22,.8);backdrop-filter:blur(20px);z-index:10}
.dw-chat__header-icon{width:40px;height:40px;border-radius:12px;overflow:hidden;box-shadow:0 2px 10px rgba(212,175,110,.2);flex-shrink:0}
.dw-chat__header-icon img{width:100%;height:100%;object-fit:cover}
.dw-chat__header-name{font-size:16px;font-weight:700;color:var(--ivory);letter-spacing:.04em}
.dw-chat__header-sub{font-size:10.5px;color:rgba(212,175,110,.5);font-weight:500;margin-top:1px}
.dw-chat__header-status{margin-left:auto;display:flex;align-items:center;gap:5px}
.dw-chat__header-dot{width:7px;height:7px;border-radius:50%;background:#4ade80;box-shadow:0 0 6px rgba(74,222,128,.4)}
.dw-chat__header-status span{font-size:10px;color:rgba(245,239,230,.3);font-weight:500}

.dw-chat__progress{padding:0 18px;margin-top:8px}
.dw-chat__progress>div:first-child,.dw-chat__progress{position:relative}
.dw-chat__progress::before{content:'';display:block;height:2px;border-radius:1px;background:rgba(212,175,110,.08)}
.dw-chat__progress-bar{position:absolute;top:0;left:18px;height:2px;border-radius:1px;background:linear-gradient(90deg,var(--gold),var(--gold-dk));transition:width .5s ease}

.dw-chat__body{flex:1;overflow-y:auto;padding:16px 14px 8px}
.dw-chat__footer{padding:10px 18px 24px;text-align:center;background:rgba(26,20,22,.6);backdrop-filter:blur(10px);border-top:1px solid rgba(212,175,110,.05);z-index:10}
.dw-chat__footer{font-size:10px;color:rgba(212,175,110,.25);letter-spacing:.04em}

.dw-bubble{display:flex;margin-bottom:12px}
.dw-bubble--bot{justify-content:flex-start}
.dw-bubble--user{justify-content:flex-end}
.dw-bubble__avatar{width:32px;height:32px;border-radius:50%;overflow:hidden;margin-right:8px;margin-top:2px;flex-shrink:0;box-shadow:0 2px 8px rgba(212,175,110,.15)}
.dw-bubble__avatar img{width:100%;height:100%;object-fit:cover}
.dw-bubble__body{max-width:78%;padding:11px 15px;font-size:13.5px;line-height:1.7;white-space:pre-line}
.dw-bubble__body--bot{border-radius:2px 16px 16px 16px;background:rgba(255,255,255,.06);border:1px solid rgba(212,175,110,.08);color:rgba(245,239,230,.8);font-weight:400}
.dw-bubble__body--user{border-radius:16px 2px 16px 16px;background:linear-gradient(135deg,var(--gold),var(--gold-dk));color:var(--dark);font-weight:600;box-shadow:0 2px 10px rgba(212,175,110,.25)}

.dw-typing{display:flex;gap:5px;align-items:center;padding:13px 18px!important}
.dw-typing__dot{width:7px;height:7px;border-radius:50%;background:var(--gold);opacity:.4;animation:dwTyping 1.2s ease infinite}
.dw-typing__dot:nth-child(2){animation-delay:.2s}
.dw-typing__dot:nth-child(3){animation-delay:.4s}

.dw-chips{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:12px;padding-left:40px}
.dw-chip{padding:9px 16px;border-radius:22px;border:1.5px solid rgba(212,175,110,.15);background:rgba(255,255,255,.04);color:rgba(245,239,230,.7);font-size:12.5px;font-weight:600;cursor:pointer;transition:all .2s ease;font-family:var(--fb);backdrop-filter:blur(6px)}
.dw-chip:hover{border-color:rgba(212,175,110,.35);background:rgba(212,175,110,.08)}
.dw-chip--active{border-color:var(--gold)!important;background:linear-gradient(135deg,var(--gold),var(--gold-dk))!important;color:var(--dark)!important}
.dw-chip--confirm{background:linear-gradient(135deg,var(--gold),var(--gold-dk))!important;color:var(--dark)!important;border:none!important;box-shadow:0 2px 12px rgba(212,175,110,.3);font-weight:700}

.dw-text-input{display:flex;flex-direction:column;gap:8px;margin-bottom:12px;padding-left:40px}
.dw-text-input__field{flex:1;padding:10px 15px;border-radius:22px;border:1.5px solid rgba(212,175,110,.15);background:rgba(255,255,255,.04);font-size:13px;color:var(--ivory);outline:none;font-family:var(--fb);backdrop-filter:blur(6px)}
.dw-text-input__field::placeholder{color:rgba(212,175,110,.3)}
.dw-text-input__field:focus{border-color:rgba(212,175,110,.4)}
.dw-text-input__btn{width:100%;padding:11px 0;border-radius:22px;border:none;background:linear-gradient(135deg,var(--gold),var(--gold-dk));color:var(--dark);font-size:13.5px;font-weight:700;cursor:pointer;font-family:var(--fb);box-shadow:0 2px 10px rgba(212,175,110,.25)}
.dw-text-input__btn--disabled{opacity:.3;cursor:not-allowed;box-shadow:none}

.dw-body-input{padding-left:40px;margin-bottom:12px}
.dw-body-input__row{display:flex;flex-direction:column;gap:10px;margin-bottom:10px}
.dw-body-input__field{flex:1}
.dw-body-input__label{display:block;font-size:11px;color:rgba(212,175,110,.5);font-weight:600;margin-bottom:5px;letter-spacing:.02em}
.dw-body-input__wrap{display:flex;align-items:center;gap:4px}
.dw-body-input__input{flex:1;padding:10px 12px;border-radius:14px;border:1.5px solid rgba(212,175,110,.15);background:rgba(255,255,255,.04);font-size:16px;color:var(--ivory);outline:none;font-family:var(--fb);text-align:center;-moz-appearance:textfield}
.dw-body-input__input::-webkit-outer-spin-button,.dw-body-input__input::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}
.dw-body-input__input::placeholder{color:rgba(212,175,110,.25)}
.dw-body-input__input:focus{border-color:rgba(212,175,110,.4)}
.dw-body-input__unit{font-size:13px;color:rgba(212,175,110,.4);font-weight:600;min-width:24px}
.dw-body-input__btn{width:100%;padding:11px 0;border-radius:22px;border:none;background:linear-gradient(135deg,var(--gold),var(--gold-dk));color:var(--dark);font-size:13.5px;font-weight:700;cursor:pointer;font-family:var(--fb);box-shadow:0 2px 10px rgba(212,175,110,.25)}
.dw-body-input__btn--disabled{opacity:.3;cursor:not-allowed;box-shadow:none}

.dw-photo-input{padding-left:40px;margin-bottom:12px;display:flex;flex-direction:column;gap:8px}
.dw-photo-input__grid{display:flex;gap:10px;margin-bottom:4px}
.dw-photo-input__slot{flex:1;display:flex;flex-direction:column;gap:4px}
.dw-photo-input__slot-label{font-size:10px;color:rgba(212,175,110,.4);font-weight:600;letter-spacing:.02em}
.dw-photo-input__upload{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:20px 8px;border-radius:14px;border:2px dashed rgba(212,175,110,.18);background:rgba(255,255,255,.02);cursor:pointer;transition:all .2s;aspect-ratio:3/4}
.dw-photo-input__upload:hover{border-color:rgba(212,175,110,.35);background:rgba(212,175,110,.04)}
.dw-photo-input__icon{font-size:24px;margin-bottom:4px}
.dw-photo-input__text{font-size:10.5px;color:rgba(212,175,110,.4);font-weight:500;text-align:center}
.dw-photo-input__done{position:relative;border-radius:14px;overflow:hidden;aspect-ratio:3/4}
.dw-photo-input__preview{width:100%;height:100%;object-fit:cover;display:block;border-radius:14px;border:1px solid rgba(212,175,110,.15)}
.dw-photo-input__change{position:absolute;bottom:6px;left:50%;transform:translateX(-50%);padding:4px 12px;border-radius:10px;background:rgba(26,20,22,.7);backdrop-filter:blur(6px);color:rgba(212,175,110,.7);font-size:10px;font-weight:600;cursor:pointer;border:1px solid rgba(212,175,110,.15)}
.dw-photo-input__send{padding:11px 0;border-radius:22px;border:none;background:linear-gradient(135deg,var(--gold),var(--gold-dk));color:var(--dark);font-size:13.5px;font-weight:700;cursor:pointer;font-family:var(--fb);box-shadow:0 2px 10px rgba(212,175,110,.25)}
.dw-photo-input__skip{padding:8px 0;border:none;background:transparent;color:rgba(212,175,110,.35);font-size:12px;font-weight:600;cursor:pointer;font-family:var(--fb)}

.dw-summary{margin-bottom:12px;padding-left:40px}
.dw-summary>div:first-child{margin-bottom:0}
.dw-summary{background:rgba(255,255,255,.04);border-radius:16px;padding:16px 18px;border:1px solid rgba(212,175,110,.1);backdrop-filter:blur(10px)}
.dw-summary__tag{font-size:11px;font-weight:700;color:var(--gold);text-transform:uppercase;letter-spacing:.08em;margin-bottom:10px}
.dw-summary__row{display:flex;justify-content:space-between;padding:5px 0;border-bottom:1px solid rgba(212,175,110,.06);font-size:12.5px}
.dw-summary__label{color:rgba(212,175,110,.5);font-weight:500}
.dw-summary__value{color:var(--ivory);font-weight:600}

@keyframes dwFadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
@keyframes dwTyping{0%,60%,100%{transform:translateY(0);opacity:.3}30%{transform:translateY(-4px);opacity:.9}}
::-webkit-scrollbar{width:3px}
::-webkit-scrollbar-track{background:transparent}
::-webkit-scrollbar-thumb{background:rgba(212,175,110,.15);border-radius:3px}
      
.dw-chat-img{max-width:200px;border-radius:12px;cursor:pointer;border:1px solid rgba(212,175,110,.15)}
.dw-free-chat__img-btn{display:flex;align-items:center;justify-content:center;width:36px;height:36px;border-radius:50%;background:rgba(212,175,110,.1);border:1px solid rgba(212,175,110,.15);cursor:pointer;font-size:16px;flex-shrink:0}
.dw-free-chat{display:flex;gap:6px;padding:10px 10px;border-top:1px solid rgba(212,175,110,.08);background:rgba(26,20,22,.8);backdrop-filter:blur(20px);align-items:center}
.dw-free-chat__input{flex:1;min-width:0;padding:10px 12px;border-radius:18px;border:1.5px solid rgba(212,175,110,.15);background:rgba(255,255,255,.04);font-size:13.5px;color:var(--ivory);outline:none;font-family:var(--fb);resize:none;max-height:120px;line-height:1.5;overflow-y:auto}
.dw-free-chat__input::placeholder{color:rgba(212,175,110,.3)}
.dw-free-chat__input:focus{border-color:rgba(212,175,110,.4)}
.dw-free-chat__btn{padding:10px 14px;border-radius:22px;border:none;background:linear-gradient(135deg,var(--gold),var(--gold-dk));color:var(--dark);font-size:12px;font-weight:700;cursor:pointer;font-family:var(--fb);white-space:nowrap;flex-shrink:0}

.dw-chat__header{position:relative}
.dw-chat__menu-btn{background:none;border:none;color:rgba(245,239,230,.5);font-size:20px;cursor:pointer;padding:4px 8px;margin-left:auto;font-weight:700;letter-spacing:2px}
.dw-chat__menu{position:absolute;top:100%;right:12px;background:var(--dark-m);border:1px solid rgba(212,175,110,.12);border-radius:12px;overflow:hidden;box-shadow:0 8px 24px rgba(0,0,0,.4);z-index:30;min-width:150px}
.dw-chat__menu-item{display:block;width:100%;padding:12px 16px;border:none;background:transparent;color:rgba(245,239,230,.7);font-size:13px;font-weight:500;text-align:left;cursor:pointer;font-family:var(--fb);transition:background .2s}
.dw-chat__menu-item:hover{background:rgba(212,175,110,.06)}
.dw-chat__menu-item--danger{color:#ef4444}
.dw-chat__menu-divider{height:1px;background:rgba(212,175,110,.08);margin:4px 0}
.dw-chat__menu-item--danger:hover{background:rgba(239,68,68,.06)}

.dw-notif-toast{position:fixed;top:12px;left:50%;transform:translateX(-50%);max-width:380px;width:calc(100% - 32px);padding:14px 18px;border-radius:14px;background:var(--dark-m);border:1px solid rgba(212,175,110,.15);box-shadow:0 8px 32px rgba(0,0,0,.5);z-index:100;cursor:pointer;animation:dwSlideDown .3s ease}
.dw-notif-toast__title{font-size:13px;font-weight:700;color:var(--gold);margin-bottom:3px}
.dw-notif-toast__body{font-size:12.5px;color:rgba(245,239,230,.6)}
@keyframes dwSlideDown{from{opacity:0;transform:translateX(-50%) translateY(-20px)}to{opacity:1;transform:translateX(-50%) translateY(0)}}

.dw-job-card{min-width:240px;margin:-6px -8px}
.dw-job-card__photos{display:grid;gap:2px;margin-bottom:10px;border-radius:10px;overflow:hidden}
.dw-job-card__photos--1{grid-template-columns:1fr}
.dw-job-card__photos--2{grid-template-columns:1fr 1fr}
.dw-job-card__photos--3{grid-template-columns:1fr 1fr 1fr}
.dw-job-card__photo{width:100%;height:100px;object-fit:cover;cursor:pointer;transition:opacity .2s}
.dw-job-card__photo:hover{opacity:.85}
.dw-photo-viewer{position:fixed;inset:0;background:rgba(0,0,0,.9);z-index:60;display:flex;align-items:center;justify-content:center;padding:20px;cursor:pointer}
.dw-photo-viewer__img{max-width:100%;max-height:90vh;object-fit:contain;border-radius:8px}
.dw-photo-viewer__close{position:absolute;top:16px;right:16px;width:40px;height:40px;border-radius:50%;background:rgba(255,255,255,.15);border:none;color:#fff;font-size:20px;cursor:pointer;display:flex;align-items:center;justify-content:center}
.dw-job-card__body{padding:0 4px}
.dw-job-card__header{display:flex;gap:6px;align-items:center;margin-bottom:8px;flex-wrap:wrap}
.dw-job-card__badge{display:inline-block;padding:3px 10px;border-radius:10px;background:rgba(212,175,110,.15);color:var(--gold);font-size:10px;font-weight:700;letter-spacing:.04em}
.dw-job-card__genre{display:inline-block;padding:3px 10px;border-radius:10px;background:rgba(245,239,230,.06);color:rgba(245,239,230,.5);font-size:10px;font-weight:600}
.dw-job-card__name{font-size:16px;font-weight:700;color:var(--ivory);margin-bottom:10px;letter-spacing:.02em}
.dw-job-card__details{display:flex;flex-direction:column;gap:6px;margin-bottom:10px}
.dw-job-card__detail{display:flex;align-items:center;gap:6px;font-size:13px;color:rgba(245,239,230,.7)}
.dw-job-card__icon{font-size:14px;width:20px;text-align:center;flex-shrink:0}
.dw-job-card__tags{display:flex;flex-wrap:wrap;gap:5px;margin-bottom:10px}
.dw-job-card__tag{padding:3px 9px;border-radius:10px;background:rgba(212,175,110,.08);border:1px solid rgba(212,175,110,.1);color:rgba(212,175,110,.7);font-size:10px;font-weight:600}
.dw-job-card__comment{padding-top:10px;border-top:1px solid rgba(212,175,110,.1);font-size:12.5px;color:rgba(245,239,230,.55);line-height:1.7;font-style:italic}

.dw-job-card__apply{width:100%;padding:11px 0;border-radius:12px;border:none;background:linear-gradient(135deg,var(--gold),var(--gold-dk));color:var(--dark);font-size:13px;font-weight:700;cursor:pointer;font-family:var(--fb);margin-top:10px;letter-spacing:.03em;box-shadow:0 2px 10px rgba(212,175,110,.25)}
.dw-taiken-overlay{position:fixed;inset:0;background:rgba(0,0,0,.7);backdrop-filter:blur(6px);z-index:50;display:flex;align-items:center;justify-content:center;padding:16px}
.dw-taiken-modal{width:100%;max-width:380px;background:var(--dark-m);border-radius:20px;border:1px solid rgba(212,175,110,.1);overflow:hidden;box-shadow:0 20px 60px rgba(0,0,0,.5)}
.dw-taiken-modal__header{display:flex;justify-content:space-between;align-items:center;padding:18px 20px 14px;border-bottom:1px solid rgba(212,175,110,.06)}
.dw-taiken-modal__header h3{font-size:15px;font-weight:700;color:var(--ivory)}
.dw-taiken-modal__close{width:30px;height:30px;border-radius:50%;border:1px solid rgba(212,175,110,.1);background:rgba(255,255,255,.03);color:rgba(245,239,230,.4);font-size:16px;cursor:pointer;display:flex;align-items:center;justify-content:center}
.dw-taiken-modal__body{padding:20px}
.dw-taiken-modal__shop{font-size:14px;font-weight:700;color:var(--gold);margin-bottom:10px}
.dw-taiken-modal__desc{font-size:13px;color:rgba(245,239,230,.5);line-height:1.7;margin-bottom:16px}
.dw-taiken-modal__field{margin-bottom:12px}
.dw-taiken-modal__field label{display:block;font-size:11px;font-weight:700;color:rgba(212,175,110,.6);margin-bottom:5px;letter-spacing:.02em}
.dw-taiken-modal__field input{width:100%;padding:11px 14px;border-radius:12px;border:1.5px solid rgba(212,175,110,.12);background:rgba(255,255,255,.04);color:var(--ivory);font-size:14px;outline:none;font-family:var(--fb)}
.dw-taiken-modal__field input::placeholder{color:rgba(212,175,110,.25)}
.dw-taiken-modal__field input:focus{border-color:rgba(212,175,110,.35)}
.dw-taiken-modal__btn{width:100%;padding:14px 0;border-radius:14px;border:none;background:linear-gradient(135deg,var(--gold),var(--gold-dk));color:var(--dark);font-size:14.5px;font-weight:700;cursor:pointer;font-family:var(--fb);box-shadow:0 3px 14px rgba(212,175,110,.2);margin-top:8px}
.dw-taiken-modal__done{text-align:center;padding:32px 20px}
.dw-taiken-modal__check{width:56px;height:56px;border-radius:50%;background:linear-gradient(135deg,#4ade80,#22c55e);display:flex;align-items:center;justify-content:center;margin:0 auto 16px;font-size:24px;color:#fff;box-shadow:0 4px 16px rgba(34,197,94,.25)}
.dw-taiken-modal__done h3{font-size:18px;font-weight:700;color:var(--ivory);margin-bottom:6px}
.dw-taiken-modal__done p{font-size:13px;color:rgba(245,239,230,.45);margin-bottom:20px;line-height:1.6}
`}</style>
    </div>
  );
}
